import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import loadable from '@loadable/component';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayDeliveryPickup,
  displayDeliveryShipping,
  displayPrice,
} from '../../util/configHelpers';
import { addTime, parseDateTimeString, stringifyDateToISO8601 } from '../../util/dates';
import {
  propTypes,
  AVAILABILITY_MULTIPLE_SEATS,
  LISTING_STATE_CLOSED,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_FIXED,
  LINE_ITEM_ITEM,
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_MULTIPLE_ITEMS,
  LISTING_STATE_PUBLISHED,
} from '../../util/types';
import { formatMoney, convertMoneyToNumber } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { createSlug, parse, stringify } from '../../util/urlHelpers';
import { userDisplayNameAsString } from '../../util/data';
import {
  OFFER,
  REQUEST,
  getSupportedProcessesInfo,
  isBookingProcess,
  isNegotiationProcess,
  isInquiryProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import {
  ModalInMobile,
  PrimaryButton,
  AvatarSmall,
  H1,
  H2,
  H6,
  IconCollection,
} from '../../components';
import PriceVariantPicker from './PriceVariantPicker/PriceVariantPicker';
import SubmitFinePrint from './SubmitFinePrint/SubmitFinePrint';
import EstimatedCustomerBreakdownMaybe from './EstimatedCustomerBreakdownMaybe';
import FetchLineItemsError from './FetchLineItemsError/FetchLineItemsError';
import ScalapayWidget from './ScalapayWidget/ScalapayWidget';

import css from './OrderPanel.module.css';

const { Money } = sdkTypes;
const LOW_SEATS_THRESHOLD = 10;

const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import(/* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm')
);
const BookingFixedDurationForm = loadable(() =>
  import(
    /* webpackChunkName: "BookingFixedDurationForm" */ './BookingFixedDurationForm/BookingFixedDurationForm'
  )
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm')
);

const NegotiationForm = loadable(() =>
  import(/* webpackChunkName: "NegotiationForm" */ './NegotiationForm/NegotiationForm')
);

const NegotiationRequestQuoteForm = loadable(() =>
  import(
    /* webpackChunkName: "NegotiationRequestQuoteForm" */ './NegotiationRequestQuoteForm/NegotiationRequestQuoteForm'
  )
);

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();
const ORDER_PANEL_SUBMIT_BUTTON_ID = 'orderPanelSubmitButton';

const isPublishedListing = listing => {
  return listing.attributes.state === LISTING_STATE_PUBLISHED;
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const getCheapestPriceVariant = (priceVariants = []) => {
  return priceVariants.reduce((cheapest, current) => {
    return current.priceInSubunits < cheapest.priceInSubunits ? current : cheapest;
  }, priceVariants[0]);
};

const formatMoneyIfSupportedCurrency = (price, intl) => {
  try {
    return formatMoney(intl, price);
  } catch (e) {
    return `(${price.currency})`;
  }
};

const formatSessionDates = (dates = [], intl, timeZone) => {
  if (!Array.isArray(dates) || dates.length === 0) {
    return '';
  }

  // Filter out past dates and format only future dates
  const now = new Date();
  const futureDates = dates.filter(dateValue => {
    const sessionDate = new Date(dateValue);
    return sessionDate >= now.setHours(0, 0, 0, 0); // Compare with start of today
  });

  return futureDates
    .map(dateValue => {
      const date = new Date(dateValue);
      return intl.formatDate(date, { weekday: 'short', month: 'short', day: 'numeric' });
    })
    .join(', ');
};

const parseSessionDateTime = (date, time, timeZone) => {
  if (time === '24:00') {
    const nextDay = addTime(date, 1, 'days', timeZone);
    const nextDayString = stringifyDateToISO8601(nextDay, timeZone);
    return parseDateTimeString(`${nextDayString} 00:00`, timeZone);
  }

  const dateString = stringifyDateToISO8601(date, timeZone);
  return parseDateTimeString(`${dateString} ${time}`, timeZone);
};

const getSessionBookingTimes = (session, timeZone) => {
  if (!session) return null;
  const dates = (session.dates || []).map(dateValue => new Date(dateValue)).sort((a, b) => a - b);
  if (dates.length === 0) return null;

  const startDate = dates[0];
  const endDate = dates[dates.length - 1];
  const bookingStart = parseSessionDateTime(startDate, session.startTime, timeZone);
  const bookingEnd = parseSessionDateTime(endDate, session.endTime, timeZone);

  return { bookingStart, bookingEnd };
};

const SessionSelectForm = props => {
  const {
    sessions = [],
    timeZone,
    onSubmit,
    onFetchTransactionLineItems,
    listingId,
    isOwnListing,
    isBookingRestricted = false,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    lineItems,
    marketplaceCurrency,
    sessionCurrency,
    intl,
    marketplaceName,
    processName,
    isSessionDataLoading = false,
    sessionFetchError = null,
  } = props;

  // Filter out cancelled sessions AND past sessions
  const activeSessions = sessions.filter(session => {
    if (session.status === 'cancelled') {
      return false;
    }

    // Check if any of the session dates are in the future
    const sessionDates = session.dates || [];
    const now = new Date();

    return sessionDates.some(dateValue => {
      const sessionDate = new Date(dateValue);
      const sessionDateTime = parseSessionDateTime(sessionDate, session.startTime, timeZone);
      return sessionDateTime > now;
    });
  });

  // Filter out sessions that have no seats remaining (fully booked) to prevent selecting/booking them
  const availableSessions = activeSessions.filter(session => {
    const seatsRemaining = Math.max(session.seats - (session.bookedSeats || 0), 0);
    return seatsRemaining > 0;
  });

  const [selectedSessionId, setSelectedSessionId] = useState(availableSessions[0]?.id || null);
  const [seats, setSeats] = useState(1);

  useEffect(() => {
    if (!selectedSessionId && availableSessions[0]?.id) {
      setSelectedSessionId(availableSessions[0].id);
    }
  }, [availableSessions, selectedSessionId]);

  const selectedSession = useMemo(
    () =>
      availableSessions.find(session => session.id === selectedSessionId) || availableSessions[0] || null,
    [availableSessions, selectedSessionId]
  );

  const sessionDatesKey = useMemo(() => {
    if (!selectedSession?.dates) return '';
    return selectedSession.dates
      .map(dateValue => new Date(dateValue).toISOString().slice(0, 10))
      .join(',');
  }, [selectedSession]);

  const seatsLeft = selectedSession
    ? Math.max(selectedSession.seats - (selectedSession.bookedSeats || 0), 0)
    : 0;

  useEffect(() => {
    if (selectedSession && seats > seatsLeft) {
      setSeats(Math.max(seatsLeft, 1));
    }
  }, [selectedSession, seats, seatsLeft]);

  const bookingTimes = useMemo(() => {
    return selectedSession ? getSessionBookingTimes(selectedSession, timeZone) : null;
  }, [selectedSession, sessionDatesKey, timeZone]);

  const unitPriceInSubunits = selectedSession
    ? selectedSession.price ?? selectedSession.priceInSubunits
    : null;
  const resolvedCurrency = sessionCurrency || marketplaceCurrency;

  const lastFetchKeyRef = useRef(null);

  useEffect(() => {
    if (
      !selectedSession ||
      !bookingTimes ||
      fetchLineItemsInProgress ||
      !Number.isInteger(unitPriceInSubunits) ||
      !resolvedCurrency
    ) {
      return;
    }

    const fetchKey = `${selectedSessionId}-${seats}-${bookingTimes.bookingStart.toISOString()}-${bookingTimes.bookingEnd.toISOString()}`;
    if (lastFetchKeyRef.current === fetchKey) {
      return;
    }
    lastFetchKeyRef.current = fetchKey;

    onFetchTransactionLineItems({
      orderData: {
        bookingStart: bookingTimes.bookingStart,
        bookingEnd: bookingTimes.bookingEnd,
        seats,
        unitPriceInSubunits,
        currency: resolvedCurrency,
      },
      listingId,
      isOwnListing,
    });
  }, [
    selectedSessionId,
    seats,
    bookingTimes?.bookingStart,
    bookingTimes?.bookingEnd,
    fetchLineItemsInProgress,
    onFetchTransactionLineItems,
    listingId,
    isOwnListing,
    unitPriceInSubunits,
    resolvedCurrency,
  ]);

  const handleSubmit = () => {
    if (!selectedSession || !bookingTimes || seatsLeft === 0) return;

    // Additional safety check: prevent booking past dates
    const now = new Date();
    if (bookingTimes.bookingStart <= now) {
      console.error('Cannot book past dates');
      return;
    }

    const bookingDate = formatSessionDates(selectedSession.dates, intl, timeZone);
    const bookingTime = `${selectedSession.startTime} - ${selectedSession.endTime}`;

    onSubmit({
      sessionId: selectedSession.id,
      bookingStartTime: bookingTimes.bookingStart.getTime(),
      bookingEndTime: bookingTimes.bookingEnd.getTime(),
      bookingDate,
      bookingTime,
      seats,
      unitPriceInSubunits,
      currency: resolvedCurrency,
    });
  };

  const seatsOptions = selectedSession
    ? Array.from({ length: Math.max(seatsLeft, 1) }, (_, i) => i + 1)
    : [1];

  const sessionPriceValue = selectedSession?.price ?? selectedSession?.priceInSubunits;
  const formattedPrice =
    Number.isInteger(sessionPriceValue) && resolvedCurrency
      ? formatMoney(intl, new Money(sessionPriceValue, resolvedCurrency))
      : null;

  const showEstimatedBreakdown = !isSessionDataLoading && lineItems && lineItems.length > 0;
  const hasAvailableSessions = availableSessions.length > 0;

  return (
    <div className={css.sessionSelect}>
      <div className={css.sessionSelectHeader}>
        <H6 as="h3" className={css.sessionSelectTitle}>
          {hasAvailableSessions && (
            <div className={css.sessionListCount}>{availableSessions.length}</div>
          )}
          <FormattedMessage id="OrderPanel.sessionSelectTitle" />
        </H6>
      </div>

      {isSessionDataLoading && activeSessions.length === 0 ? (
        <div className={css.sessionListEmpty}>
          {intl.formatMessage({ id: 'OrderPanel.sessionListLoading' })}
        </div>
      ) : activeSessions.length === 0 ? (
        <div className={css.sessionListEmpty}>
          {intl.formatMessage({
            id: sessionFetchError ? 'OrderPanel.sessionListError' : 'OrderPanel.sessionListEmpty',
          })}
        </div>
      ) : !hasAvailableSessions ? (
        <div className={css.sessionListEmpty}>
          {intl.formatMessage({ id: 'OrderPanel.noSlotsAvailable' })}
        </div>
      ) : (
        <div className={css.sessionCardList}>
          {availableSessions.map(session => {
            const isSelected = session.id === selectedSessionId;
            const seatsRemaining = Math.max(session.seats - (session.bookedSeats || 0), 0);
            const sessionPrice = session.price ?? session.priceInSubunits;
            const sessionPriceLabel =
              Number.isInteger(sessionPrice) && resolvedCurrency
                ? formatMoney(intl, new Money(sessionPrice, resolvedCurrency))
                : null;

            return (
              <button
                key={session.id}
                type="button"
                data-testid="session-card"
                data-session-id={session.id}
                className={classNames(css.sessionCard, {
                  [css.sessionCardSelected]: isSelected,
                })}
                onClick={() => setSelectedSessionId(session.id)}
              >
                <span
                  className={classNames(css.sessionRadio, {
                    [css.sessionRadioSelected]: isSelected,
                  })}
                  aria-hidden="true"
                />
                <div className={css.sessionCardBody}>
                  <span className={css.sessionCardTitle}>
                    {formatSessionDates(session.dates, intl, timeZone)}
                  </span>
                  <span className={css.sessionCardTime}>
                    {session.startTime} - {session.endTime}
                  </span>
                </div>
                <div className={css.sessionCardMeta}>
                  <span
                    className={classNames(css.sessionSeatsBadge, {
                      [css.sessionSeatsBadgeLow]: seatsRemaining <= 1,
                      [css.sessionSeatsBadgeMany]: seatsRemaining > 1,
                    })}
                  >
                    {intl.formatMessage(
                      { id: 'OrderPanel.sessionSelectSeatsLeft' },
                      { seats: seatsRemaining }
                    )}
                  </span>
                  {sessionPriceLabel ? (
                    <span className={css.sessionCardPrice}>{sessionPriceLabel}</span>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {hasAvailableSessions && (
        <>
          <label className={css.sessionSelectLabel}>
            <FormattedMessage id="OrderPanel.sessionSelectSeatsLabel" />
          </label>

          <select
            className={css.sessionSelectInput}
            data-testid="session-seats"
            value={seats}
            onChange={event => setSeats(Number.parseInt(event.target.value, 10))}
            disabled={!selectedSession || seatsLeft === 0}
          >
            {seatsOptions.map(option => (
              <option value={option} key={option}>
                {option}
              </option>
            ))}
          </select>
        </>
      )}

      {showEstimatedBreakdown ? (
        <div className={css.sessionSelectBreakdown}>
          <H6 as="h3" className={css.sessionSelectBreakdownTitle}>
            <FormattedMessage id="BookingTimeForm.priceBreakdownTitle" />
          </H6>
          <EstimatedCustomerBreakdownMaybe
            breakdownData={{
              startDate: bookingTimes?.bookingStart,
              endDate: bookingTimes?.bookingEnd,
            }}
            lineItems={lineItems}
            timeZone={timeZone}
            currency={marketplaceCurrency}
            marketplaceName={marketplaceName}
            processName={processName}
          />
          <div className={css.sessionSelectButtonRow}>
            <PrimaryButton
              type="button"
              onClick={handleSubmit}
              className={css.submitBtn}
              disabled={!selectedSession || !bookingTimes || seatsLeft === 0}
            >
              <FormattedMessage id="OrderPanel.ctaButtonMessageBooking" />
            </PrimaryButton>
            <div className={css.sessionSelectNote}>
              <FormattedMessage id="OrderPanel.youWontBeChargedInfo" />
            </div>
          </div>
        </div>
      ) : null}

      <FetchLineItemsError error={fetchLineItemsError} />
    </div>
  );
};

const sessionPricingMaybe = (publicData, override) => {
  const sessionPricing = override || publicData?.sessionPricing;
  const { currency, minPriceInSubunits, maxPriceInSubunits, isVariable } = sessionPricing || {};

  if (!currency || !Number.isInteger(minPriceInSubunits)) {
    return null;
  }

  return {
    currency,
    minPriceInSubunits,
    maxPriceInSubunits,
    isVariable: !!isVariable || minPriceInSubunits !== maxPriceInSubunits,
    price: new Money(minPriceInSubunits, currency),
  };
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const { orderOpen, ...searchParams } = parse(search);
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const handleSubmit = (isOwnListing, isClosed, isDirectSubmit, onSubmit, history, location) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  return isDirectSubmit
    ? () => onSubmit({})
    : () => openOrderModal(isOwnListing, isClosed, history, location);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const PriceMaybe = props => {
  const {
    price,
    publicData,
    validListingTypes,
    intl,
    marketplaceCurrency,
    sessionPricingOverride,
    showCurrencyMismatch = false,
  } = props;
  const { listingType, unitType } = publicData || {};

  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
  const hasMultiplePriceVariants = publicData?.priceVariants?.length > 1;
  const sessionPricing = sessionPricingMaybe(publicData, sessionPricingOverride);
  const priceToDisplay = sessionPricingOverride
    ? sessionPricing?.price
    : price || sessionPricing?.price;

  if (!showPrice || !priceToDisplay || (isPriceVariationsInUse && hasMultiplePriceVariants)) {
    return null;
  }

  // Get formatted price or currency code if the currency does not match with marketplace currency
  const { formattedPrice, priceTitle } = priceData(priceToDisplay, marketplaceCurrency, intl);
  const priceValue = (
    <span className={css.priceValue}>{formatMoneyIfSupportedCurrency(priceToDisplay, intl)}</span>
  );
  const pricePerUnit = (
    <span className={css.perUnit}>
      <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
    </span>
  );

  // TODO: In CTA, we don't have space to show proper error message for a mismatch of marketplace currency
  //       Instead, we show the currency code in place of the price
  return showCurrencyMismatch ? (
    <div className={css.priceContainerInCTA}>
      <div className={css.priceValueInCTA} title={priceTitle}>
        <FormattedMessage
          id="OrderPanel.priceInMobileCTA"
          values={{ priceValue: formattedPrice }}
        />
      </div>
      <div className={css.perUnitInCTA}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
    </div>
  ) : (
    <div className={css.priceContainer}>
      <p className={css.price}>
        <FormattedMessage
          id={
            sessionPricing?.isVariable ? 'OrderPanel.sessionPriceStartingFrom' : 'OrderPanel.price'
          }
          values={{ priceValue, pricePerUnit: '' }}
        />
      </p>
    </div>
  );
};

const PriceMissing = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingPriceMissing" />
    </p>
  );
};

const SessionPricingNotice = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.sessionPricingNotice" />
    </p>
  );
};
const InvalidCurrency = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
    </p>
  );
};

const InvalidPriceVariants = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingPriceVariantsAreInvalid" />
    </p>
  );
};

const hasUniqueVariants = priceVariants => {
  const priceVariantsSlugs = priceVariants?.map(variant =>
    variant.name ? createSlug(variant.name) : 'no-name'
  );
  return new Set(priceVariantsSlugs).size === priceVariants.length;
};

const hasValidPriceVariants = priceVariants => {
  const isArray = Array.isArray(priceVariants);
  const hasItems = isArray && priceVariants.length > 0;
  const variantsHaveNames = hasItems && priceVariants.every(variant => variant.name);
  const namesAreUnique = hasItems && hasUniqueVariants(priceVariants);

  return variantsHaveNames && namesAreUnique;
};

/**
 * @typedef {Object} ListingTypeConfig
 * @property {string} listingType - The type of the listing
 * @property {string} transactionType - The type of the transaction
 * @property {string} transactionType.process - The process descriptionof the transaction
 * @property {string} transactionType.alias - The alias of the transaction process
 * @property {string} transactionType.unitType - The unit type of the transaction
 */

/**
 * OrderPanel is a component that renders a panel for making bookings, purchases, or inquiries for a listing.
 * It handles different transaction processes and displays appropriate forms based on the listing type.
 *
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overwrites the default class for the root element
 * @param {string} [props.className] - Custom class that extends
 * @param {string} [props.titleClassName] - Custom class name for the title
 * @param {propTypes.listing} props.listing - The listing data (either regular or own listing)
 * @param {Array<ListingTypeConfig>} props.validListingTypes - Array of valid listing type configurations
 * @param {boolean} [props.isOwnListing=false] - Whether the listing belongs to the current user
 * @param {listingType.user|listingType.currentUser} props.author - The listing author's user data
 * @param {ReactNode} [props.authorLink] - Custom component for rendering the author link
 * @param {ReactNode} [props.payoutDetailsWarning] - Warning message about payout details
 * @param {Function} props.onSubmit - Handler for form submission
 * @param {ReactNode|string} props.title - Title of the panel
 * @param {ReactNode} [props.titleDesktop] - Alternative title for desktop view
 * @param {ReactNode|string} [props.subTitle] - Subtitle text
 * @param {Function} props.onManageDisableScrolling - Handler for managing scroll behavior
 * @param {Function} props.onFetchTimeSlots - Handler for fetching available time slots
 * @param {Object} [props.monthlyTimeSlots] - Available time slots by month
 * @param {Function} props.onFetchTransactionLineItems - Handler for fetching transaction line items
 * @param {Function} [props.onContactUser] - Handler for contacting the listing author
 * @param {Array} [props.lineItems] - Array of line items for the transaction
 * @param {boolean} props.fetchLineItemsInProgress - Whether line items are being fetched
 * @param {Object} [props.fetchLineItemsError] - Error object if line items fetch failed
 * @param {string} props.marketplaceCurrency - The currency used in the marketplace
 * @param {number} props.dayCountAvailableForBooking - Number of days available for booking
 * @param {string} props.marketplaceName - Name of the marketplace
 *
 * @returns {JSX.Element} Component that displays the order panel with appropriate form
 */
const OrderPanel = props => {
  const [mounted, setMounted] = useState(false);
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    setMounted(true);
  }, []);
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    isBookingRestricted = false,
    isInquiryRestricted = false,
    onSubmit,
    title,
    titleDesktop,
    author,
    authorLink,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    timeSlotsForDate,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    sessionPricingOverride,
    sessions = [],
    fetchLineItemsInProgress,
    fetchLineItemsError,
    fetchSessionPricingInProgress,
    fetchSessionPricingError,
    payoutDetailsWarning,
    showListingImage,
  } = props;

  const publicData = listing?.attributes?.publicData || {};
  const { listingType, unitType, transactionProcessAlias = '', priceVariants, startTimeInterval } =
    publicData || {};

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  const price = listing?.attributes?.price;
  const isInquiry = isInquiryProcess(processName);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const isNegotiation = isNegotiationProcess(processName);
  const isRestrictedBookableListing = isBooking && (isOwnListing || isBookingRestricted);
  const isRestrictedInquirableListing = isInquiry && isInquiryRestricted;
  const isPaymentProcess = isBooking || isPurchase || isNegotiation;
  const sessionPricing = sessionPricingMaybe(publicData, sessionPricingOverride);
  const sessionCurrency = sessionPricing?.currency || price?.currency || marketplaceCurrency;

  // Check if listing data and session data are still loading to prevent showing error states prematurely
  const isListingDataLoaded = listing && listing.attributes;
  const isSessionDataLoaded = !fetchSessionPricingInProgress;

  // For session-based listings, detect if they use session pricing instead of base pricing
  const isSessionBasedListing =
    publicData?.unitType === 'fixed' && publicData?.transactionProcessAlias?.includes('booking');

  // Only show error states when both listing and session data are fully loaded
  const shouldShowErrors =
    isListingDataLoaded && (isSessionBasedListing ? isSessionDataLoaded : true);

  // Updated conditions that account for session-based listings and proper loading states
  const showPriceMissing =
    shouldShowErrors &&
    isPaymentProcess &&
    !isNegotiation &&
    !price &&
    !sessionPricing &&
    !isSessionBasedListing;
  const showSessionPricingNotice =
    shouldShowErrors &&
    isPaymentProcess &&
    !isNegotiation &&
    !price &&
    !!sessionPricing &&
    !sessionPricingOverride &&
    !isSessionBasedListing;
  const showInvalidCurrency =
    shouldShowErrors &&
    isPaymentProcess &&
    !isNegotiation &&
    !sessionPricingOverride &&
    price?.currency !== marketplaceCurrency &&
    !isSessionBasedListing;

  const resolvedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Etc/UTC';
  const timeZone = listing?.attributes?.availabilityPlan?.timezone || resolvedTimeZone;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;
  const shouldShowBookingForm = isBooking || !!sessionPricingOverride;
  const shouldHaveFixedBookingDuration = isBooking && [LINE_ITEM_FIXED].includes(lineItemUnitType);
  const showBookingFixedDurationForm =
    mounted && shouldHaveFixedBookingDuration && !isClosed && timeZone && priceVariants?.length > 0;

  const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const showBookingTimeForm = mounted && shouldHaveBookingTime && !isClosed && timeZone;

  const shouldHaveBookingDates =
    isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const showBookingDatesForm = mounted && shouldHaveBookingDates && !isClosed && timeZone;
  const showFallbackBookingTimeForm =
    mounted &&
    shouldShowBookingForm &&
    !isClosed &&
    timeZone &&
    !showBookingFixedDurationForm &&
    !showBookingTimeForm &&
    !showBookingDatesForm;

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const shouldHavePurchase = isPurchase && lineItemUnitType === LINE_ITEM_ITEM;
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock = shouldHavePurchase && !isClosed && currentStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm =
    mounted && shouldHavePurchase && !isClosed && typeof currentStock === 'number';

  const showInquiryForm = mounted && !isClosed && isInquiry;
  // if listing is a request, we show the negotiation form (reverse negotiation). User (provider) needs to make an offer first.
  const showNegotiationForm = mounted && !isClosed && isNegotiation && unitType === REQUEST;
  // if listing is an offer, we show the "request a quote" form as user needs to ask for a quote first from the provider.
  const showRequestQuoteForm = mounted && !isClosed && isNegotiation && unitType === OFFER;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const { pickupEnabled, shippingEnabled } = listing?.attributes?.publicData || {};

  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);
  const allowOrdersOfMultipleItems = [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(
    listingTypeConfig?.stockType
  );

  const activeSessions = (sessions || []).filter(session => session.status !== 'cancelled');
  const hasSessions = activeSessions.length > 0;
  const showTopPrice = !hasSessions;

  const searchParams = parse(location.search);
  const isOrderOpen = !!searchParams.orderOpen;
  const preselectedPriceVariantSlug = searchParams.bookableOption;

  const seatsEnabled = [AVAILABILITY_MULTIPLE_SEATS].includes(listingTypeConfig?.availabilityType);

  // Note: publicData contains priceVariationsEnabled if listing is created with priceVariations enabled.
  const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
  const preselectedPriceVariant =
    Array.isArray(priceVariants) && preselectedPriceVariantSlug && isPriceVariationsInUse
      ? priceVariants.find(pv => pv?.name && createSlug(pv?.name) === preselectedPriceVariantSlug)
      : null;

  const priceVariantsMaybe = isPriceVariationsInUse
    ? {
        isPriceVariationsInUse,
        priceVariants,
        priceVariantFieldComponent: PriceVariantPicker,
        preselectedPriceVariant,
        isPublishedListing: isPublishedListing(listing),
      }
    : !isPriceVariationsInUse && showBookingFixedDurationForm
    ? {
        isPriceVariationsInUse: false,
        priceVariants: [getCheapestPriceVariant(priceVariants)],
        priceVariantFieldComponent: PriceVariantPicker,
      }
    : {};

  const showInvalidPriceVariantsMessage =
    shouldShowErrors && isPriceVariationsInUse && !hasValidPriceVariants(priceVariants);

  const nextSession = useMemo(() => {
    if (!activeSessions || activeSessions?.length === 0) return null;
    const now = new Date();

    const futureSessions = activeSessions
      .map(session => {
        const sessionDates = session?.dates || [];
        const earliestFutureDate = sessionDates
          .map(d => {
            const sessionDate = new Date(d);
            return parseSessionDateTime(sessionDate, session.startTime, timeZone);
          })
          .filter(dt => dt > now)
          .sort((a, b) => a - b)[0];

        const seatsRemaining = Math.max(session.seats - (session.bookedSeats || 0), 0);

        return earliestFutureDate ? { ...session, earliestFutureDate, seatsRemaining } : null;
      })
      .filter(s => s !== null);

    if (futureSessions.length === 0) return null;

    const availableSessions = futureSessions.filter(s => s.seatsRemaining > 0);
    const fullSessions = futureSessions.filter(s => s.seatsRemaining <= 0);

    if (availableSessions?.length > 0) {
      // Prioritize available sessions: pick the earliest one
      return [...availableSessions].sort((a, b) => {
        return a.earliestFutureDate - b.earliestFutureDate;
      })[0];
    } else {
      // If all are full: pick the last one (latest date)
      return [...fullSessions].sort((a, b) => {
        return b.earliestFutureDate - a.earliestFutureDate;
      })[0];
    }
  }, [activeSessions, timeZone]);

  const nextSessionBadge = useMemo(() => {
    if (!nextSession || nextSession.seatsRemaining === 0) return null;
    const { seatsRemaining, earliestFutureDate } = nextSession;
    const formattedDate = intl.formatDate(earliestFutureDate, {
      month: 'short',
      day: 'numeric',
    });
    const isLow = seatsRemaining <= LOW_SEATS_THRESHOLD;

    return (
      <div className={css.nextSessionBadgeWrapper}>
        <span className={css.nextSessionDate}>
          <IconCollection iconName="calendar" />
          {formattedDate}
        </span>
        <span
          className={classNames(css.nextSessionSpots, {
            [css.nextSessionSpotsLow]: isLow,
            [css.nextSessionSpotsMany]: !isLow,
          })}
        >
          {/* {isLow ? <IconCollection iconName="fire" /> : null} */}
          <span>
            {intl.formatMessage(
              { id: 'OrderPanel.sessionSelectSeatsLeft' },
              { seats: seatsRemaining }
            )}
            {isLow ? ' !' : ''}
          </span>
        </span>
      </div>
    );
  }, [nextSession, intl]);

  const sharedProps = {
    lineItemUnitType,
    onSubmit,
    price,
    marketplaceCurrency,
    listingId: listing.id,
    isOwnListing,
    marketplaceName,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
  };

  const showClosedListingHelpText = listing.id && isClosed;

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const authorDisplayName = userDisplayNameAsString(author, '');

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.orderTitle);
  const fixedPriceItem = lineItems?.find(i => i.code === 'line-item/fixed');
  // Same price PriceMaybe (below) shows as the listing's headline price - not a specific
  // session's price, which can differ per session and doesn't represent "the" listing price.
  const basePriceValue = sessionPricingOverride ? sessionPricing?.price : price || sessionPricing?.price;
  // Prefer the fetched line-item total (accurate once a booking has been configured), but fall
  // back to the base listing price so a price is available immediately and for listing types
  // (e.g. session-based ones) that never produce a 'line-item/fixed' line item.
  const displayPriceValue = fixedPriceItem?.lineTotal || basePriceValue;
  const getValue = displayPriceValue ? formatMoneyIfSupportedCurrency(displayPriceValue, intl) : null;
  // Pass the raw numeric amount straight from OrderPanel's own price data to the Scalapay
  // widget, instead of having it scrape and parse the formatted, locale-dependent text above.
  const scalapayAmount = displayPriceValue ? convertMoneyToNumber(displayPriceValue) : null;

  return (
    <div className={classes}>
      <ModalInMobile
        containerClassName={css.modalContainer}
        id="OrderFormInModal"
        isModalOpenOnMobile={isOrderOpen}
        onClose={() => {
          closeOrderModal(history, location);
          document.getElementById(ORDER_PANEL_SUBMIT_BUTTON_ID)?.focus();
        }}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <div className={css.modalHeading}>
          <H1 className={css.heading}>{title}</H1>
        </div>

        <div>
          <h2 className={css.perSetPrice}>{getValue}</h2>
          <p className={css.category}>{listing?.attributes?.publicData?.category || 'Formation'}</p>
          <div className={css.taxAndPlatformFeeNote}>
            <FormattedMessage id="OrderPanel.taxAndPlatformFeeExcluded" />
          </div>
          {isPaymentProcess && Number.isFinite(scalapayAmount) ? (
            <ScalapayWidget
              amount={scalapayAmount}
              locale={intl.locale?.split('-')[0]}
              className={css.scalapayWidget}
            />
          ) : null}
        </div>

        {showListingImage && (
          <div className={css.orderHeading}>
            {titleDesktop ? titleDesktop : <H2 className={titleClasses}>{title}</H2>}
            {subTitleText ? <div className={css.orderHelp}>{subTitleText}</div> : null}
          </div>
        )}
        {showTopPrice ? (
          <React.Fragment>
            <PriceMaybe
              price={price}
              publicData={publicData}
              validListingTypes={validListingTypes}
              intl={intl}
              marketplaceCurrency={marketplaceCurrency}
              sessionPricingOverride={sessionPricingOverride}
            />
            <div className={css.taxAndPlatformFeeNote}>
              <FormattedMessage id="OrderPanel.taxAndPlatformFeeExcluded" />
            </div>
          </React.Fragment>
        ) : null}

        {/* <div className={css.author}>
          <AvatarSmall user={author} className={css.providerAvatar} />
          <span className={css.providerNameLinked}>
            <FormattedMessage id="OrderPanel.author" values={{ name: authorLink }} />
          </span>
          <span className={css.providerNamePlain}>
            <FormattedMessage id="OrderPanel.author" values={{ name: authorDisplayName }} />
          </span>
        </div> */}

        {isRestrictedBookableListing ? (
          <p className={css.errorSidebar}>
            <FormattedMessage
              id={isOwnListing ? 'OrderPanel.ownListing' : 'OrderPanel.trainerBookingRestricted'}
            />
          </p>
        ) : (
          <SessionSelectForm
            sessions={activeSessions}
            timeZone={timeZone}
            onSubmit={onSubmit}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            lineItems={lineItems}
            fetchLineItemsError={fetchLineItemsError}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            marketplaceCurrency={marketplaceCurrency}
            sessionCurrency={sessionCurrency}
            intl={intl}
            marketplaceName={marketplaceName}
            processName={processName}
            isSessionDataLoading={fetchSessionPricingInProgress}
            sessionFetchError={fetchSessionPricingError}
          />
        )}

        {isRestrictedBookableListing || isRestrictedInquirableListing ? null : showPriceMissing ? (
          <PriceMissing />
        ) : showSessionPricingNotice ? (
          <SessionPricingNotice />
        ) : showInvalidCurrency ? (
          <InvalidCurrency />
        ) : showInvalidPriceVariantsMessage ? (
          <InvalidPriceVariants />
        ) : showBookingFixedDurationForm ? (
          <BookingFixedDurationForm
            seatsEnabled={seatsEnabled}
            className={css.bookingForm}
            formId="OrderPanelBookingFixedDurationForm"
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            monthlyTimeSlots={monthlyTimeSlots}
            timeSlotsForDate={timeSlotsForDate}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            startTimeInterval={startTimeInterval}
            timeZone={timeZone}
            finePrintComponent={SubmitFinePrint}
            {...priceVariantsMaybe}
            {...sharedProps}
          />
        ) : showBookingTimeForm ? (
          <BookingTimeForm
            seatsEnabled={seatsEnabled}
            className={css.bookingForm}
            formId="OrderPanelBookingTimeForm"
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            monthlyTimeSlots={monthlyTimeSlots}
            timeSlotsForDate={timeSlotsForDate}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            timeZone={timeZone}
            finePrintComponent={SubmitFinePrint}
            {...priceVariantsMaybe}
            {...sharedProps}
          />
        ) : showBookingDatesForm ? (
          <BookingDatesForm
            seatsEnabled={seatsEnabled}
            className={css.bookingForm}
            formId="OrderPanelBookingDatesForm"
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            timeZone={timeZone}
            finePrintComponent={SubmitFinePrint}
            {...priceVariantsMaybe}
            {...sharedProps}
          />
        ) : showProductOrderForm ? (
          <ProductOrderForm
            formId="OrderPanelProductOrderForm"
            currentStock={currentStock}
            allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
            pickupEnabled={pickupEnabled && displayPickup}
            shippingEnabled={shippingEnabled && displayShipping}
            displayDeliveryMethod={displayPickup || displayShipping}
            onContactUser={onContactUser}
            {...sharedProps}
          />
        ) : showInquiryForm ? (
          <InquiryWithoutPaymentForm
            formId="OrderPanelInquiryForm"
            onSubmit={onSubmit}
            finePrintComponent={SubmitFinePrint}
            isOwnListing={isOwnListing}
          />
        ) : showNegotiationForm ? (
          <NegotiationForm
            formId="OrderPanelNegotiationForm"
            onSubmit={onSubmit}
            finePrintComponent={SubmitFinePrint}
            payoutDetailsWarning={payoutDetailsWarning}
            isOwnListing={isOwnListing}
          />
        ) : showRequestQuoteForm ? (
          <NegotiationRequestQuoteForm
            formId="OrderPanelRequestQuoteForm"
            onSubmit={onSubmit}
            finePrintComponent={SubmitFinePrint}
            payoutDetailsWarning={payoutDetailsWarning}
            isOwnListing={isOwnListing}
          />
        ) : !isKnownProcess ? (
          <p className={css.errorSidebar}>
            <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
          </p>
        ) : null}
      </ModalInMobile>

      <div className={css.openOrderForm}>
        <div className={css.mobileStickyInfo}>
          <PriceMaybe
            price={price}
            publicData={publicData}
            validListingTypes={validListingTypes}
            intl={intl}
            marketplaceCurrency={marketplaceCurrency}
            sessionPricingOverride={sessionPricingOverride}
            showCurrencyMismatch
          />

          {nextSessionBadge}
        </div>

        {isRestrictedBookableListing ? (
          <div className={css.closedListingButton}>
            <FormattedMessage
              id={isOwnListing ? 'OrderPanel.ownListing' : 'OrderPanel.trainerBookingRestricted'}
            />
          </div>
        ) : isRestrictedInquirableListing ? (
          <div className={css.closedListingButton}>
            <FormattedMessage id="OrderPanel.trainerInquiryRestricted" />
          </div>
        ) : isClosed ? (
          <div className={css.closedListingButton}>
            <FormattedMessage id="OrderPanel.closedListingButtonText" />
          </div>
        ) : (
          <PrimaryButton
            id={ORDER_PANEL_SUBMIT_BUTTON_ID}
            onClick={handleSubmit(
              isOwnListing,
              isClosed,
              showInquiryForm || showNegotiationForm,
              onSubmit,
              history,
              location
            )}
            className={css.submitButton}
            disabled={isOutOfStock || showPriceMissing || showSessionPricingNotice}
          >
            {isBooking ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageBooking" />
            ) : isOutOfStock ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
            ) : isPurchase ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
            ) : showNegotiationForm ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageMakeOffer" />
            ) : showRequestQuoteForm ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageRequestAQuote" />
            ) : (
              <FormattedMessage id="OrderPanel.ctaButtonMessageInquiry" />
            )}
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};

export default OrderPanel;
